import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Autocomplete,
  Grid
} from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { TYPE_OF_TASKS_QUERY, PEOPLE_QUERY } from '../../graphql/tasks';
import { PROJECT_TREE_QUERY, ProjectsListQuery, GET_COMPANIES } from '../../graphql/projects';

const TasksFilters = ({ filters, setFilters }) => {
  const { data: typeOfTasksData } = useQuery(TYPE_OF_TASKS_QUERY, {
    fetchPolicy: 'cache-first'
  });

  const { data: peopleData } = useQuery(PEOPLE_QUERY, {
    variables: { page: 1, size: 10000 },
    fetchPolicy: 'cache-first'
  });

  const { data: projectsData } = useQuery(ProjectsListQuery, {
    variables: { pageSize: 1000, page: 1 },
    fetchPolicy: 'cache-first'
  });

  const { data: companiesData } = useQuery(GET_COMPANIES, {
    fetchPolicy: 'cache-first'
  });

  const { data: projectTreeData } = useQuery(PROJECT_TREE_QUERY, {
    variables: { projectId: filters.project },
    fetchPolicy: 'cache-first',
    skip: !filters.project
  });

  const allElements = useMemo(() => {
    const elements = {};
    if (projectTreeData?.project_tree) {
      try {
        const parsed = JSON.parse(projectTreeData.project_tree);
        const flattenTree = (items, depth = 0) => {
          items.forEach(item => {
            elements[item.id] = { ...item, depth };
            if (item.children) flattenTree(item.children, depth + 1);
          });
        };
        const itemsArray = Array.isArray(parsed) ? parsed : [parsed];
        flattenTree(itemsArray);
      } catch (error) {
        console.error('Error parsing project tree:', error);
      }
    }
    return elements;
  }, [projectTreeData]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Preserve pagination and sorting params
    const page = params.get('page');
    const rowsPerPage = params.get('rowsPerPage');
    const sortColumn = params.get('sortColumn');
    const sortDirection = params.get('sortDirection');

    // Clear and rebuild params
    const newParams = new URLSearchParams();

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) newParams.set(key, value);
    });

    // Restore pagination and sorting
    if (page) newParams.set('page', page);
    if (rowsPerPage) newParams.set('rowsPerPage', rowsPerPage);
    if (sortColumn) newParams.set('sortColumn', sortColumn);
    if (sortDirection) newParams.set('sortDirection', sortDirection);

    const newURL = `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`;
    window.history.replaceState({}, '', newURL);
  }, [filters]);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        {/* PROJECT */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={projectsData?.projects?.list || []}
            getOptionLabel={(option) =>
              `${option.name}${option.number ? ` (${option.number})` : ''}`
            }
            value={
              projectsData?.projects?.list?.find(
                p => p.id === filters.project
              ) || null
            }
            onChange={(event, newValue) =>
              setFilters(prev => ({
                ...prev,
                project: newValue?.id || '',
                element: ''
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Project" size="small" />
            )}
          />
        </Grid>
        {/* ELEMENT */}
        <Grid item xs={12} sm={6} md={3}>

          <Autocomplete
            options={
              allElements
                ? Object.values(allElements).map(element => ({
                  ...element,
                  displayName:
                    '\u00A0\u00A0\u00A0'.repeat(element.depth || 0) +
                    (element.label || element.name),
                  cleanName: element.label || element.name
                }))
                : []
            }
            getOptionLabel={(option) => option.cleanName}
            isOptionEqualToValue={(option, value) =>
              option.id === value.id
            }
            renderOption={(props, option) => (
              <li {...props}>{option.displayName}</li>
            )}
            value={
              allElements[filters.element]
                ? {
                  ...allElements[filters.element],
                  displayName:
                    '\u00A0\u00A0\u00A0'.repeat(
                      allElements[filters.element].depth || 0
                    ) +
                    (allElements[filters.element].label ||
                      allElements[filters.element].name),
                  cleanName:
                    allElements[filters.element].label ||
                    allElements[filters.element].name
                }
                : null
            }
            onChange={(event, newValue) =>
              setFilters(prev => ({
                ...prev,
                element: newValue?.id || ''
              }))
            }
            disabled={!filters.project}
            renderInput={(params) => (
              <TextField
                {...params}
                label={!filters.project ? "Select project first" : "Element"}
                size="small"
              />
            )}
          />
        </Grid>
        {/* COMPANY */}
        <Grid item xs={12} sm={6} md={2}>

          <Autocomplete
            options={companiesData?.companies?.list || []}
            getOptionLabel={(option) => option.name}
            value={
              companiesData?.companies?.list?.find(
                c => c.id.toString() === filters.company
              ) || null
            }
            onChange={(event, newValue) =>
              setFilters(prev => ({
                ...prev,
                company: newValue?.id || ''
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Company" size="small" />
            )}
          />
        </Grid>
        {/* OWNER */}
        <Grid item xs={12} sm={6} md={2}>
          <Autocomplete
            options={peopleData?.people?.list || []}
            getOptionLabel={(option) =>
              `${option.last_name} ${option.first_name}`
            }
            value={
              peopleData?.people?.list?.find(
                p => p.id.toString() === filters.responsible
              ) || null
            }
            onChange={(event, newValue) =>
              setFilters(prev => ({
                ...prev,
                responsible: newValue?.id || ''
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Owner" size="small" />
            )}
          />
        </Grid>
        {/* CATEGORY */}
        <Grid item xs={12} sm={6} md={2}>
          <Autocomplete
            options={typeOfTasksData?.type_of_tasks || []}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={
              typeOfTasksData?.type_of_tasks?.find(
                t => t.id.toString() === filters.types.toString()
              ) || null
            }
            onChange={(event, newValue) =>
              setFilters(prev => ({
                ...prev,
                types: newValue?.id || ''
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Types" size="small" />
            )}
          />
        </Grid>
        {/* PRIORITY */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority}
              label="Priority"
              onChange={(e) =>
                setFilters(prev => ({
                  ...prev,
                  priority: e.target.value
                }))
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="0">Highest</MenuItem>
              <MenuItem value="1">High</MenuItem>
              <MenuItem value="2">Normal</MenuItem>
              <MenuItem value="3">Low</MenuItem>
              <MenuItem value="4">Lowest</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {/* DATE FROM */}
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            label="From"
            type="date"
            size="small"
            fullWidth
            value={filters.dateFrom}
            onChange={(e) =>
              setFilters(prev => ({
                ...prev,
                dateFrom: e.target.value
              }))
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        {/* DATE TO */}
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            label="To"
            type="date"
            size="small"
            fullWidth
            value={filters.dateTo}
            onChange={(e) =>
              setFilters(prev => ({
                ...prev,
                dateTo: e.target.value
              }))
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        {/* STATUS */}
        <Grid item xs={12} sm={6} md={2}>

          <Autocomplete
            options={['New', 'In progress', 'Done']}
            value={filters.complete || null}
            onChange={(event, newValue) =>
              setFilters(prev => ({
                ...prev,
                complete: newValue || ''
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Status" size="small" />
            )}
          />
        </Grid>
        {/* CLEAR BUTTON */}
        <Grid
          item
          xs={12}
          sm={6}
          md={1}
          sx={{ textAlign: 'right' }}
        >
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={() =>
              setFilters({
                name: '',
                project: '',
                element: '',
                company: '',
                types: '',
                priority: '',
                complete: '',
                responsible: '',
                dateFrom: '',
                dateTo: ''
              })
            }
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TasksFilters;
